import React, { useState } from 'react';
import { newsArticles } from '../data/mock';
import { Calendar, Tag, ArrowRight, X, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const News = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState({ type: null, message: '' });

  const categories = ['all', ...new Set(newsArticles.map(a => a.category))];

  const filteredNews = selectedCategory === 'all'
    ? newsArticles
    : newsArticles.filter(a => a.category === selectedCategory);

  const openArticle = (article) => {
    setSelectedArticle(article);
    document.body.style.overflow = 'hidden';
  };

  const closeArticle = () => {
    setSelectedArticle(null);
    document.body.style.overflow = 'auto';
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubscribing(true);
    setSubscribeStatus({ type: null, message: '' });

    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubscribeStatus({ type: 'success', message: data.message });
        setEmail('');
      } else {
        setSubscribeStatus({ type: 'error', message: data.detail || 'Something went wrong.' });
      }
    } catch (error) {
      setSubscribeStatus({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 fade-in-section">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Tag className="text-white w-6 h-6" />
            <span className="text-white/60 text-sm tracking-widest uppercase">
              Updates & Stories
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Latest <span style={{ color: '#00A672' }}>News</span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto">
            Stay in the loop with what's happening at BEKÂR. From artist milestones 
            to industry insights.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex justify-center gap-2 md:gap-3 mb-12 flex-wrap">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 md:px-6 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-300 capitalize ${
                selectedCategory === category
                  ? 'bg-white text-black'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* News Grid - Mobile Optimized */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredNews.map((article, index) => (
            <div
              key={article.id}
              className="group cursor-pointer"
              style={{ animationDelay: `${index * 100}ms` }}
              onClick={() => openArticle(article)}
            >
              <div className="relative overflow-hidden rounded-2xl mb-4 aspect-[4/3]">
                <img 
                  src={article.image} 
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="text-xs text-white/90 bg-black/50 backdrop-blur-md px-3 py-1 rounded-full">
                    {article.category}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-white/50 text-sm mb-3">
                <Calendar size={14} />
                <span>{article.date}</span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white mb-3 group-hover:text-white/80 transition-colors duration-300 line-clamp-2">
                {article.title}
              </h3>
              <p className="text-white/70 text-sm leading-relaxed mb-4 line-clamp-3">
                {article.excerpt}
              </p>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-white hover:bg-white/10 -ml-3"
              >
                Read More
                <ArrowRight size={14} className="ml-2" />
              </Button>
            </div>
          ))}
        </div>

        {/* Newsletter Section */}
        <div className="mt-24 bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-8 md:p-12 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Never Miss An Update
          </h2>
          <p className="text-lg md:text-xl text-white/70 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter for exclusive content, artist announcements, 
            and industry insights.
          </p>
          
          {subscribeStatus.type === 'success' ? (
            <div className="flex items-center justify-center gap-3 text-green-400">
              <CheckCircle2 size={24} />
              <span className="text-lg">{subscribeStatus.message}</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={isSubscribing}
                className="flex-1 px-6 py-3 bg-white/5 border border-white/10 rounded-full text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300 disabled:opacity-50"
              />
              <Button 
                type="submit"
                disabled={isSubscribing}
                className="bg-white text-black hover:bg-white/90 rounded-full px-8 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
              >
                {isSubscribing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Subscribe'
                )}
              </Button>
            </form>
          )}
          
          {subscribeStatus.type === 'error' && (
            <p className="mt-4 text-red-400 text-sm">{subscribeStatus.message}</p>
          )}
        </div>
      </div>

      {/* Article Modal */}
      {selectedArticle && (
        <div 
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
          onClick={closeArticle}
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm" />
          
          {/* Modal Content */}
          <div 
            className="relative w-full max-w-4xl mx-4 my-8 md:my-16"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeArticle}
              className="absolute -top-12 right-0 md:right-0 text-white/70 hover:text-white p-2 hover:bg-white/10 rounded-full transition-colors z-10"
            >
              <X size={24} />
            </button>

            <div className="bg-zinc-900 rounded-2xl md:rounded-3xl overflow-hidden">
              {/* Article Image */}
              <div className="relative aspect-video md:aspect-[21/9]">
                <img 
                  src={selectedArticle.image} 
                  alt={selectedArticle.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent" />
              </div>

              {/* Article Content */}
              <div className="p-6 md:p-10 -mt-16 relative">
                <span className="inline-block text-xs text-white/80 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-4">
                  {selectedArticle.category}
                </span>
                
                <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
                  {selectedArticle.title}
                </h2>
                
                <div className="flex items-center gap-2 text-white/50 text-sm mb-8">
                  <Calendar size={14} />
                  <span>{selectedArticle.date}</span>
                </div>

                <div className="prose prose-invert max-w-none">
                  {selectedArticle.fullContent.split('\n\n').map((paragraph, idx) => (
                    <p key={`paragraph-${idx}`} className="text-white/80 text-base md:text-lg leading-relaxed mb-6">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <div className="mt-10 pt-6 border-t border-white/10">
                  <Button 
                    onClick={closeArticle}
                    className="bg-white text-black hover:bg-white/90 rounded-full px-8"
                  >
                    Back to News
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
